import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { Invoice } from "../types/shared-module-types";
import {
  dateFormat,
  dateFormatEntryEdit,
  dateFormatLocalToSort,
  dateFormatWithHoursForExcelQuery,
  decodeURIComponentSafe,
} from "./formatters";
import { EntryListView } from "../../TimesModule/types/times-module-types";
import { getUserNameFromArray } from "../../TimesModule/utils/timesUtils";

// Billing
export const transformData = (invoices: Invoice[]) => {
  let response = invoices.map((invoice: any) => {
    return {
      "Invoice Id": invoice.id,
      "QuickBooks Id": invoice.quickBooksNumber,
      "Invoice Date": dateFormat(invoice.date),
      Client: invoice.account.name,
      "Project/WO": `${invoice.billingGroup.type.name} - #${invoice.billingGroup.externalId} - ${invoice.billingGroup.name}`,
      Owner: invoice.owner.fullName,
      Amount: invoice.amount,
      Status: invoice.status.name,
      "Status Change Date": dateFormatLocalToSort(invoice.status.updatedAt),
    };
  });
  return response;
};

export const transformDataActiveItxClients = (reportInfo: any[]) => {
  let response = reportInfo.map((elem: any) => {
    return {
      "Client Id": elem.clientId,
      "Client Name": elem.clientName,
      "Client Owner": elem.accountManager,
    };
  });
  return response;
};

const getColsByReport = (excelData: any, reportType: string) => {
  let ws = XLSX.utils.json_to_sheet(excelData);
  switch (reportType) {
    case "invoices":
      // columns width
      ws["!cols"] = [
        { wpx: 60 },
        { wpx: 80 },
        { wpx: 70 },
        { wpx: 180 },
        { wpx: 240 },
        { wpx: 140 },
        { wpx: 60 },
        { wpx: 70 },
        { wpx: 130 },
      ];
      break;
    case "activeITXclients":
      // columns width
      ws["!cols"] = [{ wpx: 80 }, { wpx: 250 }, { wpx: 150 }];
      break;
    default:
      break;
  }
  return ws;
};

export const exportToExcel = async (
  excelData: any,
  fileName: any,
  reportType: string
) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spredsheetml.sheet;charset=UFT-8";
  const fileExtension = ".xlsx";

  let ws = getColsByReport(excelData, reportType);

  const wb = { Sheets: { Clients: ws }, SheetNames: ["Clients"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, fileName + fileExtension);
};

// Query Results
export const transformDataQueryResults = (
  users: any,
  list: EntryListView[]
) => {
  // ASUITE11-2740: sorted info for export excel by date, projectId and task (entityDescription)
  const sortedData = [...list].sort((a, b) => {
    const dateA = new Date(a.entryDate);
    const dateB = new Date(b.entryDate);

    if (dateA < dateB) return -1;
    if (dateA > dateB) return 1;

    if (a.projectId < b.projectId) return -1;
    if (a.projectId > b.projectId) return 1;

    return a.entityDescription.localeCompare(b.entityDescription);
  });

  let response = sortedData.map((timeEntry: EntryListView) => {
    let pwo =
      timeEntry.categoryId !== 3
        ? "Project " + timeEntry.projectId + " - " + timeEntry.projectName
        : "Work Order " +
          (timeEntry.projectId ?? timeEntry.entityId) +
          " - " +
          timeEntry.entityDescription;

    return {
      "Client Name": timeEntry.clientName,
      "Project/Work Order": pwo,
      Task: timeEntry.entityDescription,
      User: getUserNameFromArray(users, timeEntry.userId),
      Date: { v: new Date(timeEntry.entryDate), t: "d" },
      Hours: { v: timeEntry.hours, t: "n", z: "0.00" },
      "Task Type": timeEntry.taskType,
      Description: decodeURIComponentSafe(timeEntry.description),
      Billable: timeEntry.timeEntryType,
      Price: { v: timeEntry.price, t: "n", z: "$#,##0.00" },
      "Price Level / Non-Billable Reason":
        timeEntry.userPriceLevel +
        (timeEntry.nonBillableReason
          ? " / " + timeEntry.nonBillableReason
          : ""),
      Status: timeEntry.status,
      "Entered By": timeEntry.enteredBy,
      "Entered Date": timeEntry.enteredDate
        ? dateFormatWithHoursForExcelQuery(timeEntry.enteredDate)
        : "No date provided",
      "Approved By": timeEntry.approvedBy,
      "Approved Date": timeEntry.approvedDate
        ? dateFormatWithHoursForExcelQuery(timeEntry.approvedDate)
        : "No date provided",
    };
  });
  return response;
};

// Summary Query Results
export const transformDataSummaryQueryResults = (summary: any) => {
  return {
    "Total Hours": { v: summary.totalHours, t: "n", z: "0.00" },
    "Billable Hours": { v: summary.billableHours, t: "n", z: "0.00" },
    "Financed Hours": { v: summary.financedHours, t: "n", z: "0.00" },
    "Non-Billable Hours": { v: summary.nonBillableHours, t: "n", z: "0.00" },
    "Internal Hours": { v: summary.internalHours, t: "n", z: "0.00" },
    "Time Off Hours": { v: summary.timeOffHours, t: "n", z: "0.00" },
  };
};

export const exportToExcelQueryResults = async (
  filters: any,
  listFiltersTouched: any,
  excelQueryData: any,
  excelSummaryData: any,
  fileName: any
) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spredsheetml.sheet;charset=UFT-8";
  const fileExtension = ".xlsx";

  // Create empty worksheet
  let wsQuery = XLSX.utils.aoa_to_sheet([]);
  let wsSummary = XLSX.utils.aoa_to_sheet([]);

  // Add filters row at A1
  const newRow = [
    [
      "Filters Applied",
      "From: " +
        dateFormatEntryEdit(filters.startDate) +
        " - " +
        dateFormatEntryEdit(filters.endDate),
    ],
  ];
  listFiltersTouched.forEach((element) => {
    newRow[0].push(element.name + ": " + element.value);
  });

  XLSX.utils.sheet_add_aoa(wsQuery, newRow, { origin: "A1" });
  XLSX.utils.sheet_add_aoa(wsSummary, newRow, { origin: "A1" });
  // Add JSON data starting from A3
  XLSX.utils.sheet_add_json(wsQuery, excelQueryData, {
    origin: "A3",
    skipHeader: false,
  });
  XLSX.utils.sheet_add_json(wsSummary, [excelSummaryData], {
    origin: "A3",
    skipHeader: false,
  });

  // columns width
  wsQuery["!cols"] = [
    { wpx: 160 },
    { wpx: 140 },
    { wpx: 200 },
    { wpx: 100 },
    { wpx: 90 },
    { wpx: 65 },
    { wpx: 100 },
    { wpx: 200 },
    { wpx: 80 },
    { wpx: 65 },
    { wpx: 110 },
    { wpx: 80 },
    { wpx: 100 },
    { wpx: 120 },
    { wpx: 100 },
    { wpx: 120 },
  ];
  wsSummary["!cols"] = [
    { wpx: 75 },
    { wpx: 85 },
    { wpx: 85 },
    { wpx: 85 },
    { wpx: 85 },
    { wpx: 85 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsQuery, "Query");
  XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, fileName + fileExtension);
};

const getColumnValueFromHeader = (prop: string) => {
  switch (prop) {
    case "Company ID":
      return "companyId";
    case "Worker ID":
      return "workerId";
    case "Pay Component":
      return "payComponent";
    case "Rate Number":
      return "rateNumber";
    case "Hours":
      return "hours";
    case "Check Seq Number":
      return "checkSeqNumber";
    default:
      return "";
  }
};

export const exportToCSV = (
  data: any,
  filename: string,
  isSalaried: boolean
) => {
  const headers = isSalaried
    ? [
        "Company ID",
        "Worker ID",
        "Org",
        "Job Number",
        "Pay Component",
        "Rate",
        "Rate Number",
        "Hours",
        "Units",
        "Line Date",
        "Amount",
        "Check Seq Number",
        "Override State",
        "Override Local",
        "Override Local Jurisdiction",
        "Labor Override",
        "Check Template",
        "Output Type",
        "Net Pay",
        "Check Number",
        "FIT Amt",
        "SS Amt",
        "Medicare Amt",
        "Fed Backup Withholding Amt",
        "State Tax Jurisdiction",
        "SIT Amount",
        "SDI Amt",
        "SUI Amt",
        "State Misc Amt",
        "State Surcharge Amt",
        "State WC Amt",
        "State Backup Withholding Amt",
        "State Allocation %",
        "Local Tax Jurisdiction",
        "Local Tax Name",
        "Local Amt",
        "Local Allocation %",
      ]
    : ["Company ID", "Worker ID", "Pay Component", "Hours", "Rate", "Rate #"];

  // Armar el contenido CSV
  const csvRows: string[] = [];
  if (isSalaried) {
    csvRows.push("2", headers.join(",")); // primera fila: 2 mas columnas vacias y encabezados
  } else {
    csvRows.push(headers.join(","));
  }

  // recorrer cada fila
  for (const row of data) {
    const values = headers.map((h) => {
      const value = row[getColumnValueFromHeader(h)] ?? "";
      // escapamos comas o comillas
      const escaped =
        typeof value === "string" &&
        (value.includes(",") || value.includes('"'))
          ? `"${value.replaceAll('"', '""')}"`
          : value;
      return escaped;
    });
    csvRows.push(values.join(","));
  }

  const csvContent = csvRows.join("\n");
  // Descargar el archivo en navegador
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  FileSaver.saveAs(blob, filename + ".csv");
};
